import { brandsData } from '../api/handlers/genericData';

/** The mock supplier-portal org (Ningbo Garment Co. — matches suppliersApi.ts). */
export const SETTINGS_USERS_ORG_ID = 63;

/**
 * Backend wire shape (backend DD): single `role` + `status`, brand access via
 * brands[]. BE confirmed a user always carries exactly one role — `roles` is
 * an optional alternate single-element shape some responses use instead of
 * `role`; the FE falls back to it when `role` is absent (see `config-cols.tsx`).
 */
export type SettingsUserRecord = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'supplierAdmin' | 'supplierUser';
  roles?: string[];
  status: 'active' | 'inactive' | 'invited';
  accessToAllBrands: boolean;
  brands: { id: number; name: string }[];
};

/** Role wire value → the backend display name (`RoleNames.cs`) the GET returns. */
const ROLE_WIRE_TO_NAME: Record<SettingsUserRecord['role'], string> = {
  supplierAdmin: 'Supplier Admin',
  supplierUser: 'Supplier User',
};

/**
 * Serializes an internal record to the exact shape the real
 * `GET /api/organizations/{orgId}/users` returns (legacy `CustomUserResponseFormatter`
 * / `UserResponseDto`): `roles` as display-name array, PascalCase `status`,
 * `assignedBrands`. Keeps the mock's list output in lockstep with production so the
 * FE (which now consumes that shape) is exercised against the real contract.
 */
export const toWireSettingsUser = (record: SettingsUserRecord) => ({
  id: record.id,
  firstName: record.firstName,
  lastName: record.lastName,
  email: record.email,
  name: `${record.firstName} ${record.lastName}`.trim(),
  roles: [ROLE_WIRE_TO_NAME[record.role]],
  status: (record.status.charAt(0).toUpperCase() + record.status.slice(1)) as
    | 'Active'
    | 'Inactive'
    | 'Invited',
  accessToAllBrands: record.accessToAllBrands,
  assignedBrands: record.brands,
});

const firstBrand = brandsData[0] ? [{ id: brandsData[0].id, name: brandsData[0].name }] : [];

/** brandIds → brand rows (name resolved from the mock brands list). */
const brandIdsToBrands = (brandIds: number[] | undefined): { id: number; name: string }[] =>
  (brandIds ?? []).map(id => ({ id, name: brandsData.find(b => b.id === id)?.name ?? '' }));

/** Seed matching the dl-60445 prototype (one invited row, one partial-access row). */
const SEED: SettingsUserRecord[] = [
  {
    id: 'u-63-1',
    firstName: 'Wang',
    lastName: 'Lei',
    email: 'wang.lei@ningbogarment.com',
    role: 'supplierAdmin',
    status: 'active',
    accessToAllBrands: true,
    brands: [],
  },
  {
    id: 'u-63-2',
    firstName: 'Li',
    lastName: 'Hua',
    email: 'li.hua@ningbogarment.com',
    role: 'supplierUser',
    status: 'active',
    accessToAllBrands: false,
    brands: firstBrand,
  },
  {
    // `role` absent, `roles` used instead — exercises the FE's fallback path
    // (getUserRoles) and the live API's display-cased label ("Supplier Admin").
    id: 'u-63-3',
    firstName: 'Chen',
    lastName: 'Min',
    email: 'chen.min@ningbogarment.com',
    role: 'supplierAdmin',
    roles: ['Supplier Admin'],
    status: 'active',
    accessToAllBrands: true,
    brands: [],
  },
  {
    id: 'u-63-4',
    firstName: 'Zhao',
    lastName: 'Yan',
    email: 'zhao.yan@ningbogarment.com',
    role: 'supplierUser',
    status: 'invited',
    accessToAllBrands: false,
    brands: [],
  },
];

/** Survives hard reload — Mirage DB is recreated each page load (same pattern as the Ningbo profile). */
const STORAGE_KEY = '__mock_supplier_63_users__';

const clone = (records: SettingsUserRecord[]): SettingsUserRecord[] =>
  records.map(record => ({ ...record }));

/** Current supplier-settings users (localStorage-backed seed). */
export const readSettingsUsers = (): SettingsUserRecord[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return clone(SEED);
    const parsed = JSON.parse(raw) as SettingsUserRecord[];
    return Array.isArray(parsed) && parsed.length ? parsed : clone(SEED);
  } catch {
    return clone(SEED);
  }
};

const persist = (records: SettingsUserRecord[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
  } catch {
    /* best-effort — unavailable localStorage must not crash the mock */
  }
};

/** Mirrors the shipped `SupplierUserInviteDto` — brand access is derived from `role`, never sent. */
export type InviteInput = {
  firstName: string;
  lastName: string;
  email: string;
  role?: 'supplierAdmin' | 'supplierUser';
  brandIds?: number[];
};

export type InviteOutcome =
  | { result: 'created' | 'resent'; record: SettingsUserRecord }
  | { result: 'duplicate' };

/**
 * Invite command's 3-way dispatch on the mock store:
 * - existing Active/Inactive email → duplicate (409)
 * - existing Invited email → re-invite (no new row)
 * - new email → append an Invited row
 */
export const addInvitedSettingsUser = (input: InviteInput): InviteOutcome => {
  const email = input.email.trim();
  const records = readSettingsUsers();
  const existing = records.find(user => user.email.toLowerCase() === email.toLowerCase());

  if (existing) {
    if (existing.status === 'invited') return { result: 'resent', record: existing };
    return { result: 'duplicate' };
  }

  const role = input.role ?? 'supplierUser';
  const accessToAllBrands = role === 'supplierAdmin';
  const record: SettingsUserRecord = {
    id: `u-63-${records.length + 1}-${Date.now()}`,
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    email,
    role,
    status: 'invited',
    accessToAllBrands,
    brands: accessToAllBrands ? [] : brandIdsToBrands(input.brandIds),
  };
  persist([...records, record]);
  return { result: 'created', record };
};

export type UpdateInput = {
  firstName?: string;
  lastName?: string;
  role?: 'supplierAdmin' | 'supplierUser';
  accessToAllBrands?: boolean;
  brandIds?: number[];
  isActive?: boolean;
};

/**
 * Updates a supplier-settings user in place (Edit flow). Returns the updated
 * record, or `null` when the id is not a settings user (so the caller can fall
 * back to the generic user handler). `isActive` maps to `status` (invited rows
 * keep their status).
 */
export const updateSettingsUser = (id: string, patch: UpdateInput): SettingsUserRecord | null => {
  const records = readSettingsUsers();
  const index = records.findIndex(user => user.id === id);
  if (index === -1) return null;

  const current = records[index];
  const role = patch.role ?? current.role;
  const accessToAllBrands = patch.accessToAllBrands ?? current.accessToAllBrands;
  const merged: SettingsUserRecord = {
    ...current,
    firstName: patch.firstName?.trim() ?? current.firstName,
    lastName: patch.lastName?.trim() ?? current.lastName,
    role,
    accessToAllBrands,
    brands: accessToAllBrands
      ? []
      : patch.brandIds
        ? brandIdsToBrands(patch.brandIds)
        : current.brands,
    status:
      current.status === 'invited' || patch.isActive == null
        ? current.status
        : patch.isActive
          ? 'active'
          : 'inactive',
  };

  const next = [...records];
  next[index] = merged;
  persist(next);
  return merged;
};
